import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { VoidFunctionComponent } from 'react';

import { useParams } from '../../../contexts/RouterContext';
import usePackageDetails from '../../../hooks/api/packages/usePackageDetails';
import usePackageMetadata, { getPackageMetadataQueryKey } from '../../../hooks/api/packages/usePackageMetadata';
import Button from '../../generic/Button/Button';
import FallbackImage from '../../generic/FallbackImage';
import QualityRequired from '../../generic/Gates/QualityRequired';
import ModalManagePackageLabels from '../../modals/ModalManagePackageLabels';
import { useManagePackageLabelsModal } from '../../projects/dependenciesV2/hooks/useManagePackageLabelsModal';
import type { PackageInventoryQueryParams } from '../../../helpers/packageInventory';
import type { PackageInventoryParams } from '../PackageInventoryContainer';

import BlockPackageModal from './BlockPackageModal';
import PackageDetailsMetadata from './PackageDetailsMetadata';
import PackageDetailsTable from './PackageDetailsTable';

/**
 * Package Details is rendered inside the package inventory page
 * when there is an active packageLocator. If there isn't one,
 * the parent component (PackageInventoryContainer) is responsible
 * for setting it.
 */
type Props = {
  hasPackageListError: boolean;
  hasNoPackages: boolean;
  hasFilterApplied: boolean;
  resetFilters: () => void;
  query: PackageInventoryQueryParams;
};

const PackageDetails: VoidFunctionComponent<Props> = ({
  hasPackageListError,
  hasNoPackages,
  hasFilterApplied,
  resetFilters,
  query,
}) => {
  const params = useParams<PackageInventoryParams>();
  const queryClient = useQueryClient();
  const managePackageLabelsModal = useManagePackageLabelsModal();
  const packageDetailsQuery = usePackageDetails(params.packageLocator, query);
  const packageMetadataQuery = usePackageMetadata(params.packageLocator, query);

  const [isBlockModalDisplayed, setIsBlockModalDisplayed] = useState(false);
  const [isBlockingAllVersions, setIsBlockingAllVersions] = useState(false);
  //  `isInitialLoading` rather than `isLoading`: with no packageLocator both queries are disabled,
  //  and a disabled query still reports `isLoading: true`, which would hide the "no packages" state.
  const isPackageDetailsPageLoading = packageDetailsQuery.isInitialLoading || packageMetadataQuery.isInitialLoading;
  //  A metadata failure is not page-fatal; the version table is still usable without it.
  const hasPackageErrors = hasPackageListError || packageDetailsQuery.isError;
  const packageMetadata = packageMetadataQuery.data;

  if (hasPackageErrors) {
    return (
      <FallbackImage src="error-plug" alt="Network error" hasPadding>
        <div>Oops! There was an error.</div>
        <div>Please try again in a bit.</div>
      </FallbackImage>
    );
  }

  if (!isPackageDetailsPageLoading && hasNoPackages) {
    return (
      <FallbackImage src="no-results" alt="No packages found" hasPadding>
        <div>No packages found.</div>
        {hasFilterApplied && <Button onClick={resetFilters}>Reset filters</Button>}
      </FallbackImage>
    );
  }

  const onShowBlockModal = () => setIsBlockModalDisplayed(true);

  //  This function is specific to displaying the "Block Packages" button in the metadata section,
  //  since that will cause ALL future versions of the packaged to be denied.
  //  We do this by toggling the `isBlockingAllVersions`, which tells the BlockPackageModal
  //  to send 'ALL' instead of the selectedVersions list.
  const onShowBlockAllModal = () => {
    setIsBlockingAllVersions(true);
    onShowBlockModal();
  };

  const onHideBlockModal = () => {
    if (isBlockingAllVersions) {
      setIsBlockingAllVersions(false);
    }
    setIsBlockModalDisplayed(false);
  };

  const onShowManageLabelsModal = () => {
    managePackageLabelsModal.onOpen({
      context: { revision: params.packageLocator, title: packageMetadata?.name },
    });
  };

  //  The labels modal has no save callback, so refetch the metadata (which carries the labels) on close.
  const onHideManageLabelsModal = () => {
    managePackageLabelsModal.onClose();
    void queryClient.invalidateQueries({ queryKey: getPackageMetadataQueryKey(params.packageLocator, query) });
  };

  return (
    <div className="package-inventory__details">
      <PackageDetailsMetadata
        isLoading={isPackageDetailsPageLoading}
        packageMetadata={packageMetadata}
        packageDetails={packageDetailsQuery.data}
        onShowBlockAllModal={onShowBlockAllModal}
        onShowManageLabelsModal={onShowManageLabelsModal}
      />
      <PackageDetailsTable
        packageLocator={params.packageLocator}
        isLoading={isPackageDetailsPageLoading}
        packageDetails={packageDetailsQuery.data}
        onShowBlockModal={onShowBlockModal}
      />
      <QualityRequired>
        <BlockPackageModal
          isDisplayed={isBlockModalDisplayed}
          onHide={onHideBlockModal}
          isBlockingAllVersions={isBlockingAllVersions}
          packageLocator={params.packageLocator as string}
          query={query}
          packageMetadata={packageMetadata}
        />
      </QualityRequired>
      <ModalManagePackageLabels
        show={managePackageLabelsModal.isOpen}
        onHide={onHideManageLabelsModal}
        context={managePackageLabelsModal.context}
        isGlobalEnabled
        isProjectEnabled={false}
        isRevisionEnabled={false}
      />
    </div>
  );
};

export default PackageDetails;
